import { Box, ButtonBase, Typography } from '@mui/material';
import { useTranslation } from '../../../hooks/useTranslation';

interface WorkflowSidebarStep {
  title: string;
  description: string;
}

interface WorkflowSidebarProps {
  title: string;
  steps: WorkflowSidebarStep[];
  currentStep: number;
  onStepClick?: (index: number) => void;
  incompleteSteps?: boolean[];
}

const WorkflowSidebar: React.FC<WorkflowSidebarProps> = ({
  title,
  steps,
  currentStep,
  onStepClick,
  incompleteSteps,
}) => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        width: { xs: '100%', md: 280 },
        flexShrink: 0,
        p: 2.5,
        borderRadius: 3,
        bgcolor: '#F1F4FF',
        border: '1px solid rgba(24, 65, 255, 0.08)',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>
        {title}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {steps.map((step, index) => {
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          const isIncomplete = !!incompleteSteps?.[index];
          const isClickable = !!onStepClick;

          return (
            <ButtonBase
              key={`${step.title}-${index}`}
              onClick={isClickable ? () => onStepClick(index) : undefined}
              disabled={!isClickable}
              focusRipple={isClickable}
              aria-label={
                isIncomplete
                  ? t('{{step}} (incomplete)', { step: step.title })
                  : step.title
              }
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                borderRadius: 2,
                cursor: isClickable ? 'pointer' : 'default',
                '&.Mui-disabled': { cursor: 'default' },
              }}
            >
              <Box
                sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    minWidth: 24,
                    height: 24,
                    borderRadius: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: isActive || isCompleted ? '#FFFFFF' : '#6B7280',
                    bgcolor: isActive || isCompleted ? '#1841FF' : '#FFFFFF',
                    border:
                      isActive || isCompleted ? 'none' : '1px solid #D7DCE8',
                    boxShadow: isActive
                      ? '0 6px 16px rgba(24, 65, 255, 0.28)'
                      : 'none',
                  }}
                >
                  {index + 1}
                  {isIncomplete && (
                    <Box
                      data-testid="step-warning-dot"
                      sx={{
                        position: 'absolute',
                        top: -3,
                        right: -3,
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        bgcolor: '#DC2626',
                        border: '1.5px solid #F1F4FF',
                      }}
                    />
                  )}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isActive ? 700 : 600,
                      color: isActive ? '#0F172A' : '#2F3A52',
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.25,
                      color: '#6B7280',
                      lineHeight: 1.35,
                    }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
};

export default WorkflowSidebar;
